import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PaymentStatus = 'successful' | 'pending' | 'failed';

export interface PaymentItem {
  id: string;
  transactionId: string;
  clientId: string;
  clientName: string;
  constituency: string;
  planName: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  gateway: 'Razorpay' | 'Stripe' | 'UPI' | 'Bank Transfer' | 'Cashfree';
  paymentMethod: string;
  date: string;
  failureReason?: string;
  invoiceId?: string;
}

// ---- Live owner API (GET /owner/payments, GET /owner/invoices) ----

export type OwnerPaymentStatus = 'successful' | 'pending' | 'failed';

// One row = one payment attempt (order). Client fields are null if the account was deleted.
export interface OwnerPaymentItem {
  order_id: string;
  client_name: string | null;
  phone: string | null;
  post: string | null;
  village: string | null;
  samiti: string | null;
  district: string | null;
  state: string | null;
  plan_name: string | null;
  duration_days: number | null;
  // All money fields are in paise
  amount_paise: number;
  promo_code: string | null;
  original_amount_paise: number | null;
  discount_paise: number | null;
  payment_gateway: string;
  created_at: string;
  paid_at: string | null;
  status: OwnerPaymentStatus;
  raw_status: string;
  // null until the invoice job runs (up to ~5 minutes after payment)
  invoice_number: string | null;
}

// One row = one invoice; client and plan details are a copy taken at creation
export interface OwnerInvoiceItem {
  id: string;
  invoice_number: string;
  order_id: string;
  admin_id: string | null;
  client_name: string | null;
  phone: string | null;
  post: string | null;
  village: string | null;
  samiti: string | null;
  district: string | null;
  state: string | null;
  plan_name: string | null;
  duration_days: number | null;
  amount_paise: number;
  promo_code: string | null;
  original_amount_paise: number | null;
  discount_paise: number | null;
  currency: string;
  payment_gateway: string;
  paid_at: string;
  invoice_date: string;
  created_at: string;

  // ---- Tax invoice fields (all optional: an older backend does not send them yet) ----
  // The backend works these out; the panel only prints them, never recalculates GST.
  // PAID, or NO PAYMENT DUE when a 100% promo code made the amount 0
  status?: string;
  // Online, or "Not applicable (100% promo code)"
  payment_mode?: string;
  // Intra-State (customer in Rajasthan) or Inter-State
  supply_type?: 'Intra-State' | 'Inter-State' | string;
  // The customer's State with its GST code, e.g. "Rajasthan (08)"
  place_of_supply?: string;
  reverse_charge?: boolean;
  customer_type?: string;
  customer_gstin?: string;
  // One line in English; read live, so null if the customer's account was deleted
  customer_address?: string | null;
  customer_email?: string | null;
  // The one line sold: plan name + service name
  service?: string;
  sac?: string;
  quantity?: number;
  subscription_starts_at?: string | null;
  subscription_ends_at?: string | null;
  // All in paise. GST is inside the amount paid: total = taxable + cgst + sgst + igst
  plan_price_paise?: number;
  // plan_price_paise - total_paise, never negative. Use this, not discount_paise (can be null)
  invoice_discount_paise?: number;
  discount_label?: string;
  taxable_paise?: number;
  gst_percent?: number;
  cgst_paise?: number;
  sgst_paise?: number;
  igst_paise?: number;
  total_paise?: number;
  amount_in_words?: string;
  // "Paid on", or "Activated on" for a free order
  paid_on_label?: string;
  notes?: string[];
}

// The firm that issues every invoice; sent once at the top of GET /owner/invoices
export interface InvoiceSeller {
  name: string;
  address_lines: string[];
  gstin: string;
  udyam_no: string;
  phone: string;
  email: string;
  website: string;
  bank_name: string;
  bank_account_name: string;
  bank_account_no: string;
  bank_ifsc: string;
}

export interface PaymentCounts {
  all: number;
  successful: number;
  pending: number;
  failed: number;
}

export interface PagedList<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
}

export interface PaymentState {
  // Mock data, still used by the dashboard Overview only
  payments: PaymentItem[];
  paymentList: PagedList<OwnerPaymentItem> & {
    // Tab badges: cover ALL payments, unaffected by status filter or search
    counts: PaymentCounts;
  };
  invoiceList: PagedList<OwnerInvoiceItem> & {
    // null until the backend sends it (older backend)
    seller: InvoiceSeller | null;
  };
}

const emptyList = (): PagedList<any> => ({
  items: [],
  total: 0,
  limit: 25,
  offset: 0,
  loading: false,
  error: null,
});

const initialState: PaymentState = {
  payments: [],
  paymentList: { ...emptyList(), counts: { all: 0, successful: 0, pending: 0, failed: 0 } },
  invoiceList: { ...emptyList(), seller: null },
};

type ListKey = 'paymentList' | 'invoiceList';

const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    setPaymentListLoading: (state, action: PayloadAction<{ key: ListKey; loading: boolean }>) => {
      state[action.payload.key].loading = action.payload.loading;
    },
    setPaymentListError: (state, action: PayloadAction<{ key: ListKey; error: string | null }>) => {
      state[action.payload.key].error = action.payload.error;
    },
    setPaymentsPage: (
      state,
      action: PayloadAction<{
        items: OwnerPaymentItem[];
        counts: PaymentCounts;
        total: number;
        limit: number;
        offset: number;
      }>
    ) => {
      Object.assign(state.paymentList, action.payload);
    },
    setInvoicesPage: (
      state,
      action: PayloadAction<{
        items: OwnerInvoiceItem[];
        total: number;
        limit: number;
        offset: number;
        seller: InvoiceSeller | null;
      }>
    ) => {
      Object.assign(state.invoiceList, action.payload);
    },
  },
});

export const { setPaymentListLoading, setPaymentListError, setPaymentsPage, setInvoicesPage } =
  paymentSlice.actions;

export default paymentSlice.reducer;
